/**
 * GRIDHEIMR — Bot Controls Component
 *
 * Provides lifecycle controls for bot instances (start, stop, restart, delete).
 */

import React, { useState } from "react";
import type { Bot } from "../api/types";
import { BotStatus } from "../api/types";
import { startBot, stopBot, restartBot, deleteBot } from "../api/bots";
import { getErrorMessage } from "../api/client";
import { BLUE, YELLOW, GREEN, RED, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface BotControlsProps {
  bot: Bot;
  onBotUpdated?: (bot: Bot) => void;
  onBotDeleted?: () => void;
}

/**
 * Bot Controls Component
 *
 * Displays action buttons for controlling bot lifecycle.
 */
export function BotControls({ bot, onBotUpdated, onBotDeleted }: BotControlsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const canStart = bot.status === BotStatus.STOPPED;
  const canStop = bot.status === BotStatus.RUNNING;
  const canRestart = bot.status === BotStatus.RUNNING;
  const canDelete = bot.status === BotStatus.STOPPED || bot.status === BotStatus.ERROR;

  /**
   * Handle start bot action
   */
  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const updatedBot = await startBot(bot.id);
      onBotUpdated?.(updatedBot);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle stop bot action
   */
  const handleStop = async () => {
    setLoading(true);
    setError(null);
    try {
      const updatedBot = await stopBot(bot.id);
      onBotUpdated?.(updatedBot);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle restart bot action
   */
  const handleRestart = async () => {
    setLoading(true);
    setError(null);
    try {
      const updatedBot = await restartBot(bot.id);
      onBotUpdated?.(updatedBot);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle delete bot action
   */
  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await deleteBot(bot.id);
      onBotDeleted?.();
    } catch (err) {
      setError(getErrorMessage(err));
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Bot Controls</h3>

      {/* Error Message */}
      {error && (
        <div style={styles.errorBanner}>
          <span style={styles.errorIcon}>⚠️</span>
          <span style={styles.errorText}>{error}</span>
          <button
            style={styles.dismissButton}
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {/* Control Buttons */}
      <div style={styles.buttonsGrid}>
        {canStart && (
          <button
            style={{ ...styles.button, ...styles.startButton }}
            onClick={handleStart}
            disabled={loading}
          >
            <span style={styles.buttonIcon}>▶️</span>
            <span style={styles.buttonText}>
              {loading ? "Starting..." : "Start Bot"}
            </span>
          </button>
        )}

        {canStop && (
          <button
            style={{ ...styles.button, ...styles.stopButton }}
            onClick={handleStop}
            disabled={loading}
          >
            <span style={styles.buttonIcon}>⏸️</span>
            <span style={styles.buttonText}>
              {loading ? "Stopping..." : "Stop Bot"}
            </span>
          </button>
        )}

        {canRestart && (
          <button
            style={{ ...styles.button, ...styles.restartButton }}
            onClick={handleRestart}
            disabled={loading}
          >
            <span style={styles.buttonIcon}>🔄</span>
            <span style={styles.buttonText}>
              {loading ? "Restarting..." : "Restart Bot"}
            </span>
          </button>
        )}

        {canDelete && !showDeleteConfirm && (
          <button
            style={{ ...styles.button, ...styles.deleteButton }}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={loading}
          >
            <span style={styles.buttonIcon}>🗑️</span>
            <span style={styles.buttonText}>Delete Bot</span>
          </button>
        )}
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div style={styles.confirmBox}>
          <p style={styles.confirmText}>
            Are you sure you want to delete <strong>{bot.name}</strong>?
            This action cannot be undone.
          </p>
          <div style={styles.confirmButtons}>
            <button
              style={{ ...styles.confirmButton, ...styles.confirmCancelButton }}
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              style={{ ...styles.confirmButton, ...styles.confirmDeleteButton }}
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Yes, Delete"}
            </button>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div style={styles.helpText}>
        <p style={styles.helpParagraph}>
          <strong>Start:</strong> Launch the bot instance and begin processing.
        </p>
        <p style={styles.helpParagraph}>
          <strong>Stop:</strong> Gracefully shutdown the bot instance.
        </p>
        <p style={styles.helpParagraph}>
          <strong>Restart:</strong> Stop and start the bot to apply updates.
        </p>
        <p style={styles.helpParagraph}>
          <strong>Delete:</strong> Permanently destroy the bot instance and free resources.
        </p>
      </div>
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
  },
  title: {
    margin: "0 0 1rem 0",
    fontFamily: PIXEL,
    fontSize: "0.6rem",
    fontWeight: 400,
    color: YELLOW,
    letterSpacing: "0.08em",
    textShadow: `0 0 10px rgba(254,204,0,0.3)`,
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.3)",
    padding: "1rem",
    marginBottom: "1rem",
  },
  errorIcon: {
    fontSize: "1.25rem",
  },
  errorText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "#fca5a5",
  },
  dismissButton: {
    backgroundColor: "transparent",
    border: "none",
    color: "#fca5a5",
    cursor: "pointer",
    fontSize: "1.25rem",
    padding: "0.25rem",
    lineHeight: 1,
  },
  buttonsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1px",
    background: `rgba(0,106,167,0.06)`,
    border: `1px solid rgba(0,106,167,0.12)`,
    marginBottom: "1.5rem",
  },
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    padding: "1rem 1.5rem",
    border: "none",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    transition: "all 0.2s",
    backgroundColor: "transparent",
    letterSpacing: "0.08em",
    background: "#000a12",
  },
  buttonIcon: {
    fontSize: "1.5rem",
  },
  buttonText: {
    lineHeight: 1,
  },
  startButton: {
    color: GREEN,
    borderLeft: `3px solid ${GREEN}`,
  },
  stopButton: {
    color: YELLOW,
    borderLeft: `3px solid ${YELLOW}`,
  },
  restartButton: {
    color: BLUE,
    borderLeft: `3px solid ${BLUE}`,
  },
  deleteButton: {
    color: RED,
    borderLeft: `3px solid ${RED}`,
  },
  confirmBox: {
    background: "rgba(239,68,68,0.08)",
    border: `1px solid rgba(239,68,68,0.3)`,
    padding: "1.5rem",
    marginBottom: "1.5rem",
  },
  confirmText: {
    margin: "0 0 1rem 0",
    fontFamily: MONO,
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.87)",
  },
  confirmButtons: {
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-end",
  },
  confirmButton: {
    padding: "10px 20px",
    border: "1px solid",
    cursor: "pointer",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    transition: "all 0.2s",
    letterSpacing: "0.08em",
  },
  confirmCancelButton: {
    backgroundColor: "transparent",
    borderColor: "rgba(0,106,167,0.3)",
    color: "rgba(255,255,255,0.6)",
  },
  confirmDeleteButton: {
    backgroundColor: RED,
    borderColor: RED,
    color: "white",
  },
  helpText: {
    background: "rgba(0,106,167,0.04)",
    border: `1px solid rgba(0,106,167,0.12)`,
    padding: "1rem",
  },
  helpParagraph: {
    margin: "0 0 0.5rem 0",
    fontFamily: MONO,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.5)",
    lineHeight: 1.5,
  },
};
